/**
 * Whether a user holds a permission.
 *
 * WHY THIS IS ITS OWN FILE WITH ITS OWN TESTS
 * -------------------------------------------
 * This logic used to be four lines inlined in `auth-context`, and it was wrong
 * in two ways at once. It read `permission.resource` and `permission.actions`
 * off values that are plain `"resource:action"` strings, so it returned false
 * for everything a user could actually do — invisibly, because a permission
 * check that wrongly says no removes a button rather than raising anything.
 * And it reached into `permissions` without checking it was there, so a user
 * object that arrived without the field threw during render and took the whole
 * application down to a blank page.
 *
 * Both are the kind of mistake a single test would have caught, and neither was
 * reachable by a test while the logic lived inside a provider's `useCallback`.
 *
 * THE SHAPE, AND WHY IT IS A STRING
 * ---------------------------------
 * `ms-xpsims-auth` stores permissions as records but transmits them as codes:
 * `"students:read"`, `"finance:write"`. Both `POST /auth/login` and
 * `GET /users/me` send that same flat list, and so does the JWT. Matching the
 * wire format here rather than a richer client-side shape means there is no
 * translation step to get wrong.
 */

/** A permission code as the API sends it, e.g. `"students:read"`. */
export type Permission = string;

/**
 * Tests one resource/action pair against a user's permission codes.
 *
 * A missing or absent list means no permissions, never an error. This is a
 * courtesy check that decides what the interface offers — the gateway and every
 * service enforce access independently — so the cost of getting it wrong should
 * be a control the user does not see, never a page they cannot load.
 *
 * @param permissions - The user's permission codes, if they have any
 * @param resource - The resource being acted on, e.g. `"students"`
 * @param action - The verb, e.g. `"read"`
 * @returns Whether the user holds it
 */
export function hasPermission(
    permissions: readonly Permission[] | null | undefined,
    resource: string,
    action: string
): boolean {
    if (!permissions) {
        return false;
    }

    return permissions.includes(`${resource}:${action}`);
}
